import { useCallback, useEffect, useRef, useState } from "react";

import { getLatestHomeVideos } from "../services/homeVideosService";

const DEFAULT_LIMIT = 10;

const normalizeVideos = (items) => {
  const seen = new Set();

  return (Array.isArray(items) ? items : []).filter((video) => {
    const id = video?.videoId ?? video?.videoTag;

    if (id == null) return false;

    const key = String(id);

    if (seen.has(key)) return false;

    seen.add(key);

    return true;
  });
};

export const useHomeVideos = ({ limit = DEFAULT_LIMIT } = {}) => {
  const safeLimit =
    Number.isSafeInteger(Number(limit)) && Number(limit) > 0
      ? Math.min(Number(limit), 50)
      : DEFAULT_LIMIT;

  const [videos, setVideos] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const requestIdRef = useRef(0);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, []);

  const getHomeVideos = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    setLoading(true);
    setError("");

    try {
      const response = await getLatestHomeVideos({
        limit: safeLimit,
        offset: 0,
      });

      if (!mountedRef.current || requestId !== requestIdRef.current) {
        return false;
      }

      const data = response?.data;

      if (data?.success === false) {
        throw new Error(data?.message || "Unable to load videos.");
      }

      const items = Array.isArray(data?.videos)
        ? data.videos
        : Array.isArray(data?.videoThumbs)
          ? data.videoThumbs
          : Array.isArray(data)
            ? data
            : [];

      const nextVideos = normalizeVideos(items).slice(0, safeLimit);

      setVideos(nextVideos);

      setTotal(
        Number(data?.pagination?.total ?? data?.total ?? nextVideos.length),
      );

      return true;
    } catch (requestError) {
      if (!mountedRef.current || requestId !== requestIdRef.current) {
        return false;
      }

      console.error("Unable to load homepage videos:", requestError);

      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to load videos.",
      );

      return false;
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [safeLimit]);

  useEffect(() => {
    void getHomeVideos();
  }, [getHomeVideos]);

  return {
    videos,
    total,
    loading,
    error,
    getHomeVideos,
    refresh: getHomeVideos,
    hasVideos: videos.length > 0,
  };
};
